import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Formik } from 'formik';
import Spinner from '@xo-union/tk-component-spinner';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { validationSchema } from './validationSchema';
import { utcDateToString } from './helpers/utcDateToString';
import { DateForm } from './dateForm';
import { DatePrefPillGroup } from './pillGroup';
import styles from './styles.scss';
import {
  DateFormInitialVal,
  DateFormConfig,
  DateDisplayProps,
  ObjectMap,
  DatePreference,
  WeddingDatePreferences,
  SeasonName,
  UpdateWeddingDatePreferencesArgs,
} from './types';
import { weddingDatePreferenceQuery } from './graphql';
import { defaultFormat } from './helpers/dateTimeFormatted';
import { updateWeddingDatePreferencesVariantMutation } from '../../wws-onboarding/graphql/v2/mutations/updateWeddingDatePreferencesMutation';

const defaultConfigs = {
  allowFormat: true,
  showTBA: true,
  defaultView: DatePreference.None,
};

const sanitizeArgs = (args: ObjectMap): UpdateWeddingDatePreferencesArgs => {
  return Object.keys(args).reduce(
    (obj: any, key) => {
      let argValue = args[key];
      // set empty strings to null
      if (argValue === '') return { ...obj, [key]: null };
      // need to convert
      if (key === 'season') argValue = argValue as SeasonName;
      if (key === 'year') argValue = Number(argValue);
      // reverse value from UI
      if (key === 'hideCountdown') argValue = !argValue;
      // need to null out date key if preference is season
      if (key === 'date' && args.preference === DatePreference.Season) argValue = null;
      // force hideDate to be false as long as preference is not TBA
      if (key === 'hideDate' && args.preference !== DatePreference.Tba) argValue = false;

      return { ...obj, [key]: argValue };
    },
    { preference: args.preference }
  );
};

const DateDisplay = React.forwardRef<React.RefObject<any>, DateDisplayProps>(
  ({ config: clientConfig = {}, onSubmit, onError }, formikRef) => {
    const config: DateFormConfig = useMemo(() => ({ ...defaultConfigs, ...clientConfig }), [clientConfig]);

    const { data: prefs, loading } = useQuery(weddingDatePreferenceQuery);
    const [updateDatePreference] = useMutation(updateWeddingDatePreferencesVariantMutation);
    const [datePreference, setDatePreference] = useState<DatePreference | null>(null);
    const [initialValues, setInitialValues] = useState<WeddingDatePreferences>();

    const preference = useMemo(() => prefs?.weddingDatePreferences?.datePreference, [prefs]);

    const setupInitialView = useCallback((): DatePreference => {
      const pref = preference;

      // For None view, return defaultView which defaults to None
      if (!pref || pref === DatePreference.None) return config.defaultView;

      // For TBA Returns defaultView if showTBA is false
      if (!config?.showTBA && pref === DatePreference.Tba) return config.defaultView;

      return pref || config.defaultView;
    }, [config, preference]);

    useEffect(() => {
      if (!loading && prefs?.weddingDatePreferences) {
        setInitialValues(prefs.weddingDatePreferences);
      }
    }, [prefs, loading]);

    useEffect(() => {
      if (!loading && preference && datePreference === null) {
        setDatePreference(setupInitialView());
      }
    }, [loading, datePreference, preference, setupInitialView]);

    const valuesToInitialFormikValues: DateFormInitialVal = useMemo(() => {
      const res = initialValues;
      const weddingDate = res?.weddingDate || '';
      // Need to format date from DB to union date picker desired format
      const start = res?.weddingDateRangeStart ? utcDateToString(res?.weddingDateRangeStart) : weddingDate;
      const end = res?.weddingDateRangeEnd ? utcDateToString(res?.weddingDateRangeEnd) : '';
      return {
        preference: datePreference || config.defaultView,
        date: weddingDate,
        format: res?.weddingDateFormat || defaultFormat,
        rangeStart: start,
        rangeEnd: end,
        season: res?.season || '',
        year: res?.year ? String(res?.year) : '',
        hideDate: res?.hideDate || false,
        // reverse value for UI
        hideCountdown: !res?.hideCountdown,
      };
    }, [initialValues, datePreference, config.defaultView]);

    const onSubmitForm = async (values: DateFormInitialVal): Promise<void> => {
      try {
        const dateAttrs = sanitizeArgs(values);
        const {
          errors,
          data: { updateWeddingDatePreferences: updateData },
        } = await updateDatePreference({
          variables: {
            weddingDatePrefAttrs: dateAttrs,
          },
        });
        if (errors?.length) {
          throw errors;
        }
        onSubmit(updateData);
      } catch (e) {
        onError(e);
        console.error('Error updating wedding date preferences: ', e);
      }
    };

    if (loading || !prefs || datePreference === null) return <Spinner data-testid="form-spinner" />;
    return (
      <Formik
        initialValues={valuesToInitialFormikValues}
        enableReinitialize
        validationSchema={validationSchema}
        innerRef={formikRef as any}
        onSubmit={onSubmitForm}
      >
        <div className={styles.group}>
          <DatePrefPillGroup currentView={datePreference} config={config} onClick={setDatePreference} />
          <DateForm currentView={datePreference} config={config} />
        </div>
      </Formik>
    );
  }
);

export default DateDisplay;
